import React, { useCallback } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { useLang } from '../i18n'
import { useAuth } from '../auth'
import type { Role } from '../auth'
import { usePrefs } from '../prefs'
import type { FontTier } from '../prefs'
import { NotificationBell } from './NotificationBell'
import { HelpPanel } from './HelpPanel'
import { WorkLogForm } from './WorkLogForm'

interface NavTab {
  key: string
  path: string
  roles: Role[] | 'all'
}

const NAV_TABS: NavTab[] = [
  { key: 'navPersonalTasks', path: '/personal-tasks', roles: 'all' },
  { key: 'navMyPerformance', path: '/my-performance', roles: ['member'] },
  { key: 'navStats', path: '/stats', roles: ['manager', 'admin'] },
  { key: 'navDataReports', path: '/data-reports', roles: ['manager', 'admin'] },
  { key: 'navAssignments', path: '/assignments', roles: ['manager', 'admin'] },
  { key: 'navDutySchedule', path: '/duty-schedule', roles: ['manager', 'admin'] },
  { key: 'navGlobalSearch', path: '/global-search', roles: 'all' },
  { key: 'navPersonnel', path: '/personnel', roles: ['manager'] },
  { key: 'navTaskDict', path: '/task-dict', roles: ['manager', 'admin'] },
  { key: 'navAdminConsole', path: '/admin', roles: ['admin'] },
  { key: 'navAuditLog', path: '/audit-log', roles: ['manager', 'admin'] },
]

export { NAV_TABS as NAV_TABS_FOR_TEST }

function canAccess(tab: NavTab, role: Role): boolean {
  if (tab.roles === 'all') return true
  return (tab.roles as Role[]).includes(role)
}

const FONT_TIERS: FontTier[] = ['standard', 'large', 'xlarge']
const FONT_TIER_KEYS: Record<FontTier, string> = {
  standard: 'fontStandard',
  large: 'fontLarge',
  xlarge: 'fontXlarge',
}

export function NavBar() {
  const { lang, setLang, t } = useLang()
  const { user, logout } = useAuth()
  const { fontTier, theme, setFontTier, setTheme } = usePrefs()
  const navigate = useNavigate()

  const handleWorkLogSuccess = useCallback(() => {
    window.dispatchEvent(new CustomEvent('worklog:created'))
  }, [])

  const visibleTabs = user
    ? NAV_TABS.filter((tab) => canAccess(tab, user.role))
    : []

  const handleLangToggle = () => {
    setLang(lang === 'zh' ? 'en' : 'zh')
  }

  const handleThemeToggle = () => {
    setTheme(theme === 'light' ? 'green' : 'light')
  }

  const handleLogout = async () => {
    await logout()
    navigate('/login', { replace: true })
  }

  const navLinkStyle = ({ isActive }: { isActive: boolean }): React.CSSProperties => ({
    color: isActive ? 'var(--nav-text)' : 'rgba(255,255,255,0.7)',
    textDecoration: 'none',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '13px',
    background: isActive ? 'rgba(255,255,255,0.2)' : 'transparent',
    whiteSpace: 'nowrap',
  })

  const chromeBtnStyle: React.CSSProperties = {
    padding: '4px 10px',
    borderRadius: '4px',
    border: '1px solid rgba(255,255,255,0.4)',
    background: 'transparent',
    color: 'var(--nav-text)',
    cursor: 'pointer',
    fontSize: '12px',
    whiteSpace: 'nowrap',
  }

  const fontBtnStyle = (tier: FontTier): React.CSSProperties => ({
    ...chromeBtnStyle,
    padding: '4px 7px',
    background: fontTier === tier ? 'rgba(255,255,255,0.25)' : 'transparent',
    border: fontTier === tier ? '1px solid rgba(255,255,255,0.7)' : '1px solid rgba(255,255,255,0.4)',
  })

  return (
    <header
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 16px',
        minHeight: '52px',
        background: 'var(--nav-bg)',
        color: 'var(--nav-text)',
        flexShrink: 0,
        gap: '8px',
        overflowX: 'auto',
        position: 'sticky',
        top: 0,
        zIndex: 100,
      }}
    >
      <span style={{ fontSize: '16px', fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 }}>
        {t('appTitle')}
      </span>

      <nav style={{ display: 'flex', gap: '4px', alignItems: 'center', flexWrap: 'nowrap', flex: '1 1 0', minWidth: 0, overflowX: 'auto' }}>
        {visibleTabs.map((tab) => (
          <NavLink key={tab.key} to={tab.path} style={navLinkStyle}>
            {t(tab.key)}
          </NavLink>
        ))}
      </nav>

      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
        {/* Font size control */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
          {FONT_TIERS.map((tier) => (
            <button
              key={tier}
              onClick={() => setFontTier(tier)}
              title={`${t('fontSizeLabel')}: ${t(FONT_TIER_KEYS[tier])}`}
              style={fontBtnStyle(tier)}
            >
              {t(FONT_TIER_KEYS[tier])}
            </button>
          ))}
        </div>

        {/* Theme toggle */}
        <button onClick={handleThemeToggle} style={chromeBtnStyle}>
          {theme === 'light' ? t('themeGreen') : t('themeLight')}
        </button>

        {/* Language toggle */}
        <button onClick={handleLangToggle} style={chromeBtnStyle}>
          {t('langToggle')}
        </button>

        {user && (
          <>
            <WorkLogForm onSubmitSuccess={handleWorkLogSuccess} />
            <HelpPanel />
            <NotificationBell />
            <NavLink
              to="/profile"
              style={{ fontSize: '13px', color: 'rgba(255,255,255,0.8)', whiteSpace: 'nowrap', textDecoration: 'none' }}
            >
              {user.display_name}
            </NavLink>
            <button onClick={handleLogout} style={chromeBtnStyle}>
              {t('logout')}
            </button>
          </>
        )}
      </div>
    </header>
  )
}
